import {useCallback, useLayoutEffect, useMemo, useRef} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

type QueryPrimitive = string | number | boolean;
type QueryValue =
    | QueryPrimitive
    | ReadonlyArray<QueryPrimitive>
    | Record<string, QueryPrimitive | ReadonlyArray<QueryPrimitive>>;
type QueryParams = Record<string, QueryValue>;


const isPrimitiveArray = (v: unknown): v is ReadonlyArray<QueryPrimitive> =>
    Array.isArray(v) && (v.length === 0 || typeof v[0] !== 'object');

const isFlatObject = (
    v: unknown,
): v is Record<string, QueryPrimitive | ReadonlyArray<QueryPrimitive>> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

const decode = (v: string) => decodeURIComponent(v.replace(/\+/g, ' '));
const toStr = (v: QueryPrimitive): string => (typeof v === 'string' ? v : String(v));

const normalizeToString = <T extends QueryValue | QueryParams>(value: T): T => {
    if (Array.isArray(value)) return value.map(item => toStr(item as QueryPrimitive)) as unknown as T;
    if (isFlatObject(value)) {
        const out: Record<string, any> = {};
        Object.entries(value).forEach(([k, v]) => (out[k] = normalizeToString(v as any)));
        return out as T;
    }
    return toStr(value as QueryPrimitive) as T;
};

const deepEqual = (a: any, b: any): boolean => {
    if (a == null || b == null) return a === b;
    const isPrim = (x: any) => ['string', 'number', 'boolean'].includes(typeof x);
    if (isPrim(a) && isPrim(b)) return String(a) === String(b);

    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
    }
    if (typeof a === 'object' && a !== null && b !== null) {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) return false;
        return keysA.every(k => deepEqual(a[k], b[k]));
    }
    return false;
};

const parseSearch = (search: string): QueryParams => {
    const params = new URLSearchParams(search);
    const result: QueryParams = {};

    params.forEach((rawVal, rawKey) => {
        const value = decode(rawVal);

        const listMatch = rawKey.match(/^(\w+)\[\]$/);
        const nestedListMatch = rawKey.match(/^(\w+)\[(\w+)]\[\]$/);
        const nestedMatch = rawKey.match(/^(\w+)\[(\w+)]$/);

        if (nestedListMatch) {
            const [, parent, child] = nestedListMatch;
            const parentObj = (result[parent] as Record<string, QueryValue>) ?? {};
            const list = (parentObj[child] as QueryPrimitive[]) ?? [];
            parentObj[child] = [...list, value];
            result[parent] = parentObj as string[];
            return;
        }

        if (listMatch) {
            const [, key] = listMatch;
            const list = (result[key] as QueryPrimitive[]) ?? [];
            result[key] = [...list, value];
            return;
        }

        if (nestedMatch) {
            const [, parent, child] = nestedMatch;
            const parentObj = (result[parent] as Record<string, QueryValue>) ?? {};
            parentObj[child] = value;
            result[parent] = parentObj as string[];
            return;
        }

        result[rawKey] = value;
    });

    return result;
};

const stringifyParams = (params: QueryParams): string => {
    const sp = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value == null || value === '') return;

        if (isPrimitiveArray(value)) {
            value.forEach(item => sp.append(`${key}[]`, String(item)));
            return;
        }

        if (isFlatObject(value)) {
            Object.entries(value).forEach(([childKey, childVal]) => {
                if (childVal == null || childVal === '') return;

                if (isPrimitiveArray(childVal)) {
                    childVal.forEach(item => sp.append(`${key}[${childKey}][]`, String(item)));
                } else {
                    sp.append(`${key}[${childKey}]`, String(childVal));
                }
            });
            return;
        }

        sp.set(key, String(value));
    });

    return sp.toString();
};

interface UseQueryRouterOptions {
    sync?: boolean;
}

export const useQueryRouter = (
    defaults: QueryParams = {},
    {sync = true}: UseQueryRouterOptions = {},
) => {
    const history = useHistory();
    const {pathname, search, state} = useLocation();
    const strDefaults = useMemo(() => normalizeToString(defaults), [defaults]);

    useLayoutEffect(() => {
        if (!sync) return;

        const current = parseSearch(search);
        const merged = {...strDefaults, ...current};

        if (!deepEqual(current, merged)) {
            history.replace({pathname, search: stringifyParams(merged)});
        }
    }, [sync, strDefaults, search, history, pathname]);

    const queryRef = useRef<QueryParams>();
    const query = useMemo(() => {
        const parsed = parseSearch(search);
        const next = {...strDefaults, ...parsed};

        if (queryRef.current && deepEqual(queryRef.current, next)) return queryRef.current;
        queryRef.current = next;
        return next;
    }, [search, strDefaults]);

    const makeSearch = (patch: QueryParams) => stringifyParams({...query, ...patch});

    const push = useCallback((patch: QueryParams) => {
        history.push({pathname, search: makeSearch(patch)});
    }, [history, pathname, query]);

    const replace = useCallback((patch: QueryParams) => {
        history.replace({pathname, search: makeSearch(patch)});
    }, [history, pathname, query]);

    const set = push;

    const remove = useCallback((keys: string | string[]) => {
        const next = {...query} as QueryParams;
        (Array.isArray(keys) ? keys : [keys]).forEach(k => delete next[k]);
        history.replace({pathname, search: stringifyParams(next)});
    }, [history, pathname, query]);

    const reset = useCallback(() => {
        history.replace({pathname, search: stringifyParams(strDefaults)});
    }, [history, pathname, strDefaults]);

    const refresh = useCallback(() => {
        history.replace({pathname, search, state: {...state, __refresh__: Date.now()}});
    }, [history, pathname, search, state]);

    return {query, set, push, replace, remove, reset, refresh} as const;
};
